import type { MarketplaceItem, MarketplaceCategory } from "../types";
import {
  CATEGORY_EMOJI,
  CATEGORY_LABEL_KEYS,
  koinsToEur,
} from "../constants";
import { Gift, Copy, Pencil, Archive } from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";

interface ItemCardProps {
  item: MarketplaceItem;
  onEdit: (item: MarketplaceItem) => void;
  onDuplicate: (item: MarketplaceItem) => void;
  onDeactivate: (item: MarketplaceItem) => void;
  /** Optional scope/owner label (e.g. "Global" or an org name). When set, renders a badge. */
  scopeBadge?: string | null;
}

export function ItemCard({
  item,
  onEdit,
  onDuplicate,
  onDeactivate,
  scopeBadge,
}: ItemCardProps) {
  const { t } = useLocale();
  const emoji = CATEGORY_EMOJI[item.category as MarketplaceCategory] ?? "📦";
  const categoryLabelKey =
    CATEGORY_LABEL_KEYS[item.category as MarketplaceCategory];
  const categoryLabel = categoryLabelKey ? t(categoryLabelKey) : item.category;

  const inventoryText =
    item.totalInventory != null
      ? `${item.remainingInventory ?? 0} / ${item.totalInventory}`
      : t("rewards.unlimited");

  const isLowStock =
    item.totalInventory != null &&
    item.remainingInventory != null &&
    item.remainingInventory <= 5 &&
    item.remainingInventory > 0;

  const isOutOfStock =
    item.totalInventory != null && item.remainingInventory === 0;

  return (
    <div
      className={`group relative flex flex-col rounded-lg border bg-card transition-shadow hover:shadow-md ${
        !item.isActive ? "opacity-60" : ""
      }`}
    >
      {/* Image / Emoji fallback */}
      <div className="relative flex h-36 items-center justify-center overflow-hidden rounded-t-lg bg-muted">
        {item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt={item.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="text-5xl">{emoji}</span>
        )}
        {item.featured && (
          <span className="absolute right-2 top-2 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground">
            {t("rewards.featured")}
          </span>
        )}
        {!item.isActive && (
          <span className="absolute left-2 top-2 rounded-full bg-destructive px-2 py-0.5 text-[10px] font-semibold text-destructive-foreground">
            {t("rewards.deactivated")}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col gap-2 p-4">
        {scopeBadge && (
          <span
            className={`inline-flex w-fit items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
              item.scope === "GLOBAL"
                ? "bg-primary/10 text-primary"
                : "bg-muted text-muted-foreground"
            }`}
          >
            {scopeBadge}
          </span>
        )}
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-semibold text-foreground line-clamp-2">
            {item.name}
          </h3>
        </div>

        <p className="line-clamp-2 text-xs text-muted-foreground">
          {item.shortDescription || item.description}
        </p>

        {/* Price + Category */}
        <div className="mt-auto flex items-center justify-between pt-2">
          <div className="flex items-center gap-1">
            <span className="text-sm font-bold text-koins">
              {item.koinsPrice.toLocaleString()}
            </span>
            <span className="text-xs text-muted-foreground">Koins</span>
            <span className="ml-1 text-[10px] text-muted-foreground">
              (≈€{koinsToEur(item.koinsPrice)})
            </span>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
            {emoji} {categoryLabel}
          </span>
        </div>

        {/* Inventory */}
        <div className="flex items-center gap-2 text-xs">
          <Gift className="h-3 w-3 text-muted-foreground" />
          <span
            className={
              isOutOfStock
                ? "font-medium text-destructive"
                : isLowStock
                  ? "font-medium text-warning"
                  : "text-muted-foreground"
            }
          >
            {isOutOfStock
              ? t("rewards.outOfStock")
              : isLowStock
                ? t("rewards.lowStock", { inventory: inventoryText })
                : t("rewards.stock", { inventory: inventoryText })}
          </span>
        </div>
      </div>

      {/* Action buttons — visible on hover */}
      <div className="flex border-t border-border">
        <button
          type="button"
          onClick={() => onEdit(item)}
          className="flex flex-1 items-center justify-center gap-1.5 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <Pencil className="h-3 w-3" />
          {t("common.edit")}
        </button>
        <div className="w-px bg-border" />
        <button
          type="button"
          onClick={() => onDuplicate(item)}
          className="flex flex-1 items-center justify-center gap-1.5 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <Copy className="h-3 w-3" />
          {t("rewards.duplicate")}
        </button>
        {item.isActive && (
          <>
            <div className="w-px bg-border" />
            <button
              type="button"
              onClick={() => onDeactivate(item)}
              className="flex flex-1 items-center justify-center gap-1.5 py-2 text-xs font-medium text-destructive/80 transition-colors hover:bg-destructive/5 hover:text-destructive"
            >
              <Archive className="h-3 w-3" />
              {t("rewards.deactivate")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
